import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    ArrowLeft,
    Upload,
    Search,
    CheckCircle,
    Clock,
    XCircle,
    RefreshCw,
    ExternalLink,
    AlertTriangle
} from 'lucide-react';

const AdminWithdrawalsPage = () => {
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
    const [processingId, setProcessingId] = useState(null);
    const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });

    useEffect(() => {
        fetchWithdrawals();
    }, [statusFilter]);

    const fetchWithdrawals = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            params.append('limit', pagination.limit);
            params.append('offset', pagination.offset);

            const res = await api.get(`/admin/withdrawals?${params.toString()}`);
            setWithdrawals(res.data.withdrawals || []);
            setPagination(res.data.pagination || pagination);
        } catch (error) {
            console.error('Fetch withdrawals error:', error);
            toast.error('Failed to load withdrawals');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (transactionId) => {
        if (!window.confirm('Are you sure you want to approve this withdrawal? This will initiate the blockchain transaction.')) {
            return;
        }

        try {
            setProcessingId(transactionId);
            await api.post(`/admin/withdrawals/${transactionId}/approve`);
            toast.success('Withdrawal approved and processing');
            fetchWithdrawals();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to approve withdrawal');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async () => {
        if (!rejectModal.reason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            setProcessingId(rejectModal.id);
            await api.post(`/admin/withdrawals/${rejectModal.id}/reject`, {
                reason: rejectModal.reason
            });
            toast.success('Withdrawal rejected and balance refunded');
            setRejectModal({ open: false, id: null, reason: '' });
            fetchWithdrawals();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to reject withdrawal');
        } finally {
            setProcessingId(null);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-500/20 text-green-400 border-green-500/30',
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
            failed: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        const icons = {
            completed: <CheckCircle size={14} />,
            pending: <Clock size={14} />,
            processing: <RefreshCw size={14} className="animate-spin" />,
            rejected: <XCircle size={14} />,
            failed: <XCircle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
                {icons[status] || icons.pending}
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const filteredWithdrawals = withdrawals.filter(w => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            w.user?.email?.toLowerCase().includes(search) ||
            w.token?.symbol?.toLowerCase().includes(search) ||
            w.toAddress?.toLowerCase().includes(search) ||
            w.txHash?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="text-gray-400 hover:text-white transition">
                            <ArrowLeft size={24} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                                <Upload className="text-orange-400" /> Withdrawal Requests
                            </h1>
                            <p className="text-gray-400 text-sm">Manage user withdrawal requests</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchWithdrawals}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex flex-wrap gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email, token, address, or tx hash..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading withdrawals...</div>
                    ) : filteredWithdrawals.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">No withdrawals found</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Token</th>
                                        <th className="px-4 py-3 font-medium">Amount</th>
                                        <th className="px-4 py-3 font-medium">To Address</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Date</th>
                                        <th className="px-4 py-3 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredWithdrawals.map((withdrawal) => (
                                        <tr key={withdrawal.id} className="hover:bg-slate-700/30 transition">
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className="text-white font-medium">{withdrawal.user?.email || 'Unknown'}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {withdrawal.user?.firstName} {withdrawal.user?.lastName}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-2 px-2 py-1 bg-slate-700 rounded text-white font-medium">
                                                    {withdrawal.token?.symbol || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-white font-mono">
                                                {parseFloat(withdrawal.amount).toFixed(6)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {withdrawal.toAddress ? (
                                                    <span className="text-gray-300 text-xs font-mono">
                                                        {withdrawal.toAddress.slice(0, 10)}...{withdrawal.toAddress.slice(-6)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {getStatusBadge(withdrawal.status)}
                                            </td>
                                            <td className="px-4 py-3 text-gray-400 text-sm">
                                                {new Date(withdrawal.createdAt).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                {withdrawal.status === 'pending' && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleApprove(withdrawal.id)}
                                                            disabled={processingId === withdrawal.id}
                                                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition disabled:opacity-50"
                                                        >
                                                            {processingId === withdrawal.id ? '...' : 'Approve'}
                                                        </button>
                                                        <button
                                                            onClick={() => setRejectModal({ open: true, id: withdrawal.id, reason: '' })}
                                                            disabled={processingId === withdrawal.id}
                                                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition disabled:opacity-50"
                                                        >
                                                            Reject
                                                        </button>
                                                    </div>
                                                )}
                                                {withdrawal.status === 'completed' && withdrawal.txHash && (
                                                    <a
                                                        href={`https://etherscan.io/tx/${withdrawal.txHash}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                                                    >
                                                        View Tx <ExternalLink size={12} />
                                                    </a>
                                                )}
                                                {withdrawal.status === 'rejected' && withdrawal.metadata?.rejectionReason && (
                                                    <span className="text-red-400 text-xs" title={withdrawal.metadata.rejectionReason}>
                                                        {withdrawal.metadata.rejectionReason.slice(0, 20)}...
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                <div className="mt-4 text-center text-gray-400 text-sm">
                    Showing {filteredWithdrawals.length} of {pagination.total} withdrawals
                </div>
            </div>

            {/* Reject Modal */}
            {rejectModal.open && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <AlertTriangle className="text-red-400" /> Reject Withdrawal
                        </h3>
                        <p className="text-gray-400 mb-4">
                            Please provide a reason for rejecting this withdrawal. The user's balance will be refunded.
                        </p>
                        <textarea
                            value={rejectModal.reason}
                            onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                            placeholder="Enter rejection reason..."
                            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-red-500 resize-none"
                            rows={3}
                        />
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setRejectModal({ open: false, id: null, reason: '' })}
                                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={processingId}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                            >
                                {processingId ? 'Processing...' : 'Reject & Refund'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWithdrawalsPage;
